// src/services/allotmentService.js
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  addDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

class AllotmentService {
  constructor() {
    this.collectionName = 'dailyAllotments';
    this.historySubcollection = 'pricingHistory';
  }

  // Create unique allotment ID from route and date
  createAllotmentId(routeId, date) {
    return `${routeId}_${date}`;
  }

  // Get allotment for specific route and date
  async getAllotment(routeId, date) {
    try {
      const allotmentId = this.createAllotmentId(routeId, date);
      const docRef = doc(db, this.collectionName, allotmentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting allotment:', error);
      throw error;
    }
  }

  // Get allowances for date range
  async getAllowancesForDateRange(routeId, startDate, endDate) {
    try {
      const allowances = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      // Generate all dates in range (excluding checkout day)
      for (let date = new Date(start); date < end; date.setDate(date.getDate() + 1)) {
        const dateStr = date.toISOString().split('T')[0];
        const allowance = await this.getAllotment(routeId, dateStr);
        
        allowances.push({
          date: dateStr,
          routeId,
          dailyAllowance: allowance?.dailyAllowance || 0,
          baseCurrency: allowance?.baseCurrency || 'USD',
          isLocked: allowance?.isLocked || false,
          exists: !!allowance,
          // Legacy fields for backward compatibility
          allotment: allowance?.dailyAllowance || 0,
          used: 0, // Not applicable for budget allowances
          currentPrice: allowance?.dailyAllowance || 0
        });
      }
      
      return allowances;
    } catch (error) {
      console.error('Error getting allowances for date range:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  async getAllotmentsForDateRange(routeId, startDate, endDate) {
    return this.getAllowancesForDateRange(routeId, startDate, endDate);
  }

  // Create or update daily allowance
  async setAllowance(routeId, date, allowanceData, adminUser) {
    try {
      const allowanceId = this.createAllotmentId(routeId, date);
      const docRef = doc(db, this.collectionName, allowanceId);
      
      // Get existing data for history tracking
      const existing = await this.getAllotment(routeId, date);
      
      const newData = {
        routeId,
        date,
        dailyAllowance: parseFloat(allowanceData.dailyAllowance) || 0,
        baseCurrency: allowanceData.baseCurrency || 'USD',
        isLocked: allowanceData.isLocked || false,
        lastModified: serverTimestamp(),
        modifiedBy: adminUser,
        ...allowanceData
      };

      // Save the allowance
      await setDoc(docRef, newData, { merge: true });
      
      // Add pricing history if this is an update
      if (existing && (
        existing.dailyAllowance !== newData.dailyAllowance
      )) {
        await this.addPricingHistory(routeId, date, {
          previousAllowance: existing.dailyAllowance || 0,
          newAllowance: newData.dailyAllowance,
          reason: allowanceData.reason || 'Manual adjustment',
          changedBy: adminUser,
          changeType: 'manual'
        });
      }
      
      return { id: allowanceId, ...newData };
    } catch (error) {
      console.error('Error setting daily allowance:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  async setAllotment(routeId, date, allotmentData, adminUser) {
    // Convert old allotment data to new allowance format
    const allowanceData = {
      dailyAllowance: allotmentData.allotment || allotmentData.dailyAllowance || 0,
      baseCurrency: 'USD',
      isLocked: allotmentData.isLocked || false,
      reason: allotmentData.reason || 'Converted from old format'
    };
    
    return this.setAllowance(routeId, date, allowanceData, adminUser);
  }

  // Bulk update allotments
  async bulkUpdateAllotments(updates, adminUser) {
    try {
      const batch = writeBatch(db);
      const historyPromises = [];
      
      for (const update of updates) {
        const { routeId, date, ...allotmentData } = update;
        const allotmentId = this.createAllotmentId(routeId, date);
        const docRef = doc(db, this.collectionName, allotmentId);
        
        // Get existing for history
        const existing = await this.getAllotment(routeId, date);
        
        const newData = {
          routeId,
          date,
          allotment: allotmentData.allotment || 0,
          used: existing?.used || 0,
          currentPrice: allotmentData.currentPrice || 0,
          isLocked: allotmentData.isLocked || false,
          lastModified: serverTimestamp(),
          modifiedBy: adminUser,
          ...allotmentData
        };
        
        batch.set(docRef, newData, { merge: true });
        
        // Queue history update if changed
        if (existing && (
          existing.allotment !== newData.allotment || 
          existing.currentPrice !== newData.currentPrice
        )) {
          historyPromises.push(
            this.addPricingHistory(routeId, date, {
              previousPrice: existing.currentPrice,
              newPrice: newData.currentPrice,
              previousAllotment: existing.allotment,
              newAllotment: newData.allotment,
              reason: allotmentData.reason || 'Bulk update',
              changedBy: adminUser,
              changeType: 'manual'
            })
          );
        }
      }
      
      await batch.commit();
      await Promise.all(historyPromises);
      
      return { success: true, updated: updates.length };
    } catch (error) {
      console.error('Error bulk updating allotments:', error);
      throw error;
    }
  }

  // Update used count when booking is made
  async consumeAllotment(routeId, date, amount = 1) {
    try {
      const allotmentId = this.createAllotmentId(routeId, date);
      const docRef = doc(db, this.collectionName, allotmentId);
      
      const existing = await this.getAllotment(routeId, date);
      if (!existing) {
        throw new Error(`No allotment found for ${routeId} on ${date}`);
      }
      
      const newUsed = (existing.used || 0) + amount;
      
      await updateDoc(docRef, {
        used: newUsed,
        lastModified: serverTimestamp()
      });
      
      return { 
        ...existing, 
        used: newUsed,
        available: existing.allotment - newUsed
      };
    } catch (error) {
      console.error('Error consuming allotment:', error);
      throw error;
    }
  }

  // Check availability for date range
  async checkAvailability(routeId, startDate, endDate) {
    try {
      const allotments = await this.getAllotmentsForDateRange(routeId, startDate, endDate);
      
      return allotments.map(allotment => ({
        date: allotment.date,
        available: Math.max(0, allotment.allotment - allotment.used),
        total: allotment.allotment,
        used: allotment.used,
        isAvailable: (allotment.allotment - allotment.used) > 0,
        price: allotment.currentPrice
      }));
    } catch (error) {
      console.error('Error checking availability:', error);
      throw error;
    }
  }

  // Add pricing history entry
  async addPricingHistory(routeId, date, historyData) {
    try {
      const allotmentId = this.createAllotmentId(routeId, date);
      const historyRef = collection(db, this.collectionName, allotmentId, this.historySubcollection);
      
      const historyEntry = {
        timestamp: serverTimestamp(),
        ...historyData
      };
      
      await addDoc(historyRef, historyEntry);
      return historyEntry;
    } catch (error) {
      console.error('Error adding pricing history:', error);
      throw error;
    }
  }

  // Get pricing history for a date
  async getPricingHistory(routeId, date) {
    try {
      const allotmentId = this.createAllotmentId(routeId, date);
      const historyRef = collection(db, this.collectionName, allotmentId, this.historySubcollection);
      const q = query(historyRef, orderBy('timestamp', 'desc'));
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting pricing history:', error);
      throw error;
    }
  }

  // Get all allotments for a route
  async getRouteAllotments(routeId, limit = 100) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('routeId', '==', routeId),
        orderBy('date', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting route allotments:', error);
      throw error;
    }
  }

  // Delete allotment
  async deleteAllotment(routeId, date) {
    try {
      const allotmentId = this.createAllotmentId(routeId, date);
      await deleteDoc(doc(db, this.collectionName, allotmentId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting allotment:', error);
      throw error;
    }
  }

  // Get analytics for route
  async getRouteAnalytics(routeId, startDate, endDate) {
    try {
      const allotments = await this.getAllotmentsForDateRange(routeId, startDate, endDate);
      
      const analytics = {
        totalDays: allotments.length,
        totalAllotment: allotments.reduce((sum, a) => sum + a.allotment, 0),
        totalUsed: allotments.reduce((sum, a) => sum + a.used, 0),
        totalRevenue: allotments.reduce((sum, a) => sum + (a.used * a.currentPrice), 0),
        averagePrice: 0,
        utilizationRate: 0,
        daysWithBookings: allotments.filter(a => a.used > 0).length
      };
      
      analytics.averagePrice = analytics.totalUsed > 0 
        ? analytics.totalRevenue / analytics.totalUsed 
        : 0;
      
      analytics.utilizationRate = analytics.totalAllotment > 0 
        ? (analytics.totalUsed / analytics.totalAllotment) * 100 
        : 0;
      
      return analytics;
    } catch (error) {
      console.error('Error getting route analytics:', error);
      throw error;
    }
  }
}

const allotmentService = new AllotmentService();
export default allotmentService;